import React from 'react'

import { Section, SectionHeading, Button } from '../../../components/ui'
import { bookingPath, defaultWashSlug, enterBooking } from '../../../lib/booking-entry'
import { washesFrom } from '../../../lib/service-catalogue'
import { usePublishedCatalogue } from '../../../lib/use-published-catalogue'
import { readPrices, readWashes } from '../../../lib/page-prices'

import './cta.css'

// THE LAST THING THE PAGE ASKS FOR, and the page's one ink-filled primary
// action (DESIGN.md §3). Every one of the six buttons in the table above is
// an outline, so this is the only filled control between the header and the
// footer.
//
// WHAT IT REPLACES: a full-bleed navy band with a white CTA and an SVG
// triangle cut out of its bottom corner, built from raw hex. The triangle was
// the only diagonal edge on the site and the band was the only full-bleed
// colour field; neither exists in the token palette, and `--accent` and
// `--ink` are never large fills.
//
// IT ALSO CARRIES THE SURCHARGES. Honest pricing on the page beats a surprise
// on the booking screen (direction §7.3), and a visitor who has read the wash
// prices and the add-on prices should not meet another number at checkout.
// All three amounts are surcharge rows in the same catalogue as the washes and
// come off the same meta tag, so if the server has not published them the
// line still says the true thing, without inventing a figure.
//
// THERE ARE THREE OF THEM, AND THE THIRD WAS MISSING UNTIL 5 OCTOBER 2026.
// `MetaTags::Helper::FOLD_PRICES` published `large_car` and `congestion_zone`
// only, so the same-day charge (then £45) was on no page, while 85 orders in 24
// months paid it. A customer who booked for today met a number this very
// paragraph had told them did not exist. Same day is an option a customer
// chooses, not a penalty, so it reads like the other two: the thing, and what
// it adds.
//
// AND THE PARAGRAPH NO LONGER CLAIMS TO BE THE WHOLE BILL. It ended on
// "Nothing else is added to the price you picked", which is still not true
// with all three listed: the terms charge waiting time by the minute once the
// car has not been unlocked or locked (/terms_and_conditions, "Waiting time";
// /faqs quotes a different rate, which is a contradiction for Robin and not
// for this page to paper over). What the page can promise is that every one of
// these is on the screen before the card is, so that is what it says now.

const poundsOf = price => Number(String(price).replace(/[^0-9.]/g, ''))

// The cheapest wash on the page, read off the published list rather than
// typed. Every wash in that list has a price (lib/service-catalogue.js drops
// the ones that do not), so this is either the real lowest or nothing at all:
// "from £69" beside a table the page could not draw would be a claim it cannot
// see.
const lowestPrice = washes =>
  washes.length === 0 ? null : washes.map(wash => wash.price).reduce((a, b) => (poundsOf(a) <= poundsOf(b) ? a : b))

const Surcharges = ({ prices }) => {
  const large = prices.large_car
  const congestion = prices.congestion_zone
  const sameDay = prices.anytime_today

  if (large && congestion && sameDay) {
    return (
      <p className="gwmc-caption svc-cta-note">
        Large car adds {large}. Congestion Zone adds {congestion}. Need it today? Same-day for {sameDay}.
      </p>
    )
  }

  return (
    <p className="gwmc-caption svc-cta-note">
      Large cars and Congestion Zone addresses add a fixed amount, shown before you pay. Need it today?
      Same-day washes available.
    </p>
  )
}

export const ServicesCta = () => {
  const prices = readPrices()
  // The same two sources, in the same order of preference, as the comparison
  // table: the gwmc:catalogue meta tag first, /api/v2/services as the fallback.
  // "Book one of the six" used to be typed, which made it a sentence that
  // could outlive the six.
  const { entries } = usePublishedCatalogue({ fetchIfMissing: false })
  const published = readWashes()
  const washes = published.length > 0 ? washesFrom(published) : washesFrom(entries)
  const from = lowestPrice(washes)

  return (
    <Section centred background="stone" className="svc-cta">
      <SectionHeading intro="A professional valeter comes to you, wherever the car is parked, and brings their own water and power.">
        Ready when you are
      </SectionHeading>

      <Button
        className="svc-cta-btn"
        variant="primary"
        href={bookingPath(defaultWashSlug())}
        onClick={event => {
          event.preventDefault()
          enterBooking(defaultWashSlug(), 'services_final_cta')
        }}>
        {from ? `Book now from ${from}` : 'Book now'}
      </Button>

      <Surcharges prices={prices} />
    </Section>
  )
}
